import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Lightbulb, Plus, ArrowUp, User } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

function IDR(n) { return n ? new Intl.NumberFormat("id-ID", { style:"currency", currency:"IDR", maximumFractionDigits:0 }).format(n) : "—"; }

export default function BinaUsaha() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", estimated_income: "", category: "" });

  const load = () => { setLoading(true); api.get("/ideas").then(r=>setItems(r.data)).finally(()=>setLoading(false)); };
  useEffect(load, []);

  const submit = async () => {
    if (!form.title || !form.description) { toast.error("Judul & deskripsi wajib."); return; }
    try {
      await api.post("/ideas", {
        title: form.title, description: form.description,
        estimated_income: form.estimated_income ? Number(form.estimated_income) : null,
        category: form.category || null,
      });
      toast.success("Ide diajukan. Silakan ajak pengurus lain memberikan dukungan.");
      setOpen(false);
      setForm({ title: "", description: "", estimated_income: "", category: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const vote = async (id) => {
    try {
      const { data } = await api.post(`/ideas/${id}/vote`);
      toast.success(data.voted ? "Dukungan tercatat." : "Dukungan dibatalkan.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const targetGap = 1_103_100_000;
  const totalPotential = items.reduce((s,i)=>s+(i.estimated_income||0), 0);
  const gapProgress = Math.min(100, Math.round(totalPotential*100/targetGap));

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Bagian Bina Usaha</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Brainstorm & Voting Model Usaha</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Kumpulkan ide model usaha dana Klasis untuk menutup potensi kekurangan <strong>Rp 1.103.100.000</strong> hasil RAKER 2026.
            Setiap pengurus dapat mengusulkan ide dan memberi dukungan pada ide favorit.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="btn-new-idea"><Plus className="h-4 w-4 mr-2" /> Usulkan Ide</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Usulan Model Usaha Baru</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Judul model usaha</Label><Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="input-idea-title" /></div>
              <div><Label>Deskripsi & mekanisme</Label><Textarea rows={4} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} placeholder="Bagaimana model ini berjalan? Siapa target pasar? Modal awal?" data-testid="input-idea-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Kategori</Label><Input value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} placeholder="Perdagangan, Jasa, Pertanian…" data-testid="input-idea-category" /></div>
                <div><Label>Estimasi pendapatan/thn</Label><Input type="number" value={form.estimated_income} onChange={(e)=>setForm({...form, estimated_income:e.target.value})} data-testid="input-idea-income" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="btn-save-idea">Ajukan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative overflow-hidden rounded-2xl bg-navy-gradient text-white p-8 mb-6 papua-motif">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="text-[0.65rem] tracking-[0.22em] uppercase opacity-80 font-semibold">Target Bina Usaha</div>
            <div className="font-heading font-bold text-3xl mt-2">{IDR(targetGap)}</div>
          </div>
          <div>
            <div className="text-[0.65rem] tracking-[0.22em] uppercase opacity-80 font-semibold">Potensi Terkumpul</div>
            <div className="font-heading font-bold text-3xl mt-2 text-accent">{IDR(totalPotential)}</div>
            <div className="text-xs opacity-80 mt-1">{gapProgress}% dari target ({items.length} ide)</div>
          </div>
          <div>
            <div className="text-[0.65rem] tracking-[0.22em] uppercase opacity-80 font-semibold">Sumber</div>
            <div className="font-heading font-bold text-base mt-2">Keputusan RAKER 2026</div>
            <div className="text-xs opacity-80 mt-1">PIC: Bagian Bina Usaha</div>
          </div>
        </div>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center">
          <Lightbulb className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Belum ada ide. Silakan ajukan usulan pertama!</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((idea, idx) => (
            <Card key={idea.id} className={`card-hover ${idx === 0 && idea.votes_count > 0 ? "border-accent bg-accent/5" : ""}`} data-testid={`idea-${idea.id}`}>
              <CardContent className="p-5 flex items-start gap-4 flex-wrap sm:flex-nowrap">
                <button
                  onClick={() => vote(idea.id)}
                  className={`shrink-0 w-16 h-16 rounded-xl flex flex-col items-center justify-center border-2 transition-all ${
                    idea.voted_by_me
                      ? "bg-primary border-primary text-primary-foreground scale-105"
                      : "border-border hover:border-primary hover:bg-primary/5"
                  }`}
                  data-testid={`btn-vote-${idea.id}`}
                >
                  <ArrowUp className={`h-4 w-4 ${idea.voted_by_me ? "" : "text-primary"}`} />
                  <div className="font-heading font-bold text-lg mt-0.5">{idea.votes_count || 0}</div>
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {idx === 0 && idea.votes_count > 0 && <StatusBadge status="gold" label="🏆 Terpopuler" />}
                    {idea.category && <StatusBadge status="info" label={idea.category} />}
                    {idea.estimated_income && <StatusBadge status="success" label={`Est. ${IDR(idea.estimated_income)}/thn`} />}
                  </div>
                  <div className="font-heading font-bold text-base">{idea.title}</div>
                  <div className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{idea.description}</div>
                  <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
                    <User className="h-3 w-3" /> Diusulkan oleh <strong className="text-foreground/70">{idea.created_by}</strong>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
